/**
 * Subscription Test Service
 * 
 * Temporary service for testing tier functionality without Stripe integration.
 * Creates the same Firestore structure that Stripe Firebase Extension would create.
 * 
 * WARNING: Only use in development mode!
 */

import { doc, setDoc, collection, addDoc, getDoc, getDocs, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import * as tierService from './tierService';

/**
 * Simulate a subscription purchase for testing
 * @param {string} tierId - Tier ID (basic, pro, elite)
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @param {string} userId - User's Firebase UID
 * @returns {Promise<Object>} Created subscription data
 */
export async function simulateSubscription(tierId, billingPeriod, userId) {
  if (!userId) {
    throw new Error('User ID is required');
  }

  if (!['basic', 'pro', 'elite'].includes(tierId)) {
    throw new Error('Invalid tier ID. Must be basic, pro, or elite');
  }

  if (!['monthly', 'yearly'].includes(billingPeriod)) {
    throw new Error('Invalid billing period. Must be monthly or yearly');
  }

  // Get tier configuration
  const tier = tierService.getTier(tierId);
  if (!tier) {
    throw new Error(`Tier ${tierId} not found`);
  }

  // Get priceId and productId from tier config
  // Use these directly - they can be placeholders now, and will work with real Stripe IDs later
  const priceIds = tierService.getPriceIdsForTier(tierId);
  const priceId = priceIds?.[billingPeriod];
  const productId = tier.stripe?.productId;

  if (!priceId) {
    throw new Error(`Price ID not found for tier ${tierId} ${billingPeriod}`);
  }

  try {
    const now = new Date();
    const periodStart = Timestamp.fromDate(now);
    
    // Calculate period end (1 month or 1 year from now)
    const periodEndDate = new Date(now);
    if (billingPeriod === 'monthly') {
      periodEndDate.setMonth(periodEndDate.getMonth() + 1);
    } else {
      periodEndDate.setFullYear(periodEndDate.getFullYear() + 1);
    }
    const periodEnd = Timestamp.fromDate(periodEndDate);

    // Create or update customer document
    const customerRef = doc(db, 'stripe_customers', userId);
    const customerDoc = await getDoc(customerRef);
    
    const customerData = {
      id: `cus_test_${userId.substring(0, 8)}`,
      stripeId: `cus_test_${userId.substring(0, 8)}`,
      email: customerDoc.exists() ? customerDoc.data().email : null,
      metadata: {
        test: true,
        created_by: 'subscriptionTestService'
      }
    };

    await setDoc(customerRef, customerData, { merge: true });

    // Delete any existing test subscriptions first
    const subscriptionsRef = collection(db, 'stripe_customers', userId, 'subscriptions');
    const existingSubs = await getDocs(subscriptionsRef);
    
    for (const subDoc of existingSubs.docs) {
      const subData = subDoc.data();
      // Only delete test subscriptions
      if (subData.metadata?.test === true || subData.metadata?.created_by === 'subscriptionTestService') {
        await deleteDoc(subDoc.ref);
      }
    }

    // Create subscription document matching real Stripe structure
    const subscriptionData = {
      id: `sub_test_${Date.now()}`,
      status: 'active',
      created: periodStart,
      current_period_start: periodStart,
      current_period_end: periodEnd,
      cancel_at_period_end: false,
      items: {
        data: [{
          id: `si_test_${Date.now()}`,
          price: {
            id: priceId, // Use actual priceId from tier config
            product: {
              id: productId || `prod_test_${tierId}`
            },
            recurring: {
              interval: billingPeriod === 'monthly' ? 'month' : 'year'
            }
          }
        }]
      },
      metadata: {
        test: true,
        created_by: 'subscriptionTestService'
      }
    };

    const subscriptionRef = await addDoc(subscriptionsRef, subscriptionData);

    console.log(`[Test] Simulated subscription created:`, {
      tierId,
      billingPeriod,
      priceId: priceId,
      productId: productId,
      subscriptionId: subscriptionRef.id
    });

    return {
      success: true,
      subscriptionId: subscriptionRef.id,
      subscription: subscriptionData
    };
  } catch (error) {
    console.error('[Test] Error simulating subscription:', error);
    throw new Error(`Failed to simulate subscription: ${error.message}`);
  }
}

/**
 * Cancel a simulated subscription
 * @param {string} userId - User's Firebase UID
 * @returns {Promise<Object>} Cancellation result
 */
export async function cancelSimulatedSubscription(userId) {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const subscriptionsRef = collection(db, 'stripe_customers', userId, 'subscriptions');
    const existingSubs = await getDocs(subscriptionsRef);
    
    let canceledCount = 0;
    
    for (const subDoc of existingSubs.docs) {
      const subData = subDoc.data();
      // Only cancel test subscriptions
      if (subData.metadata?.test === true || subData.metadata?.created_by === 'subscriptionTestService') {
        // Update status to canceled
        await setDoc(subDoc.ref, {
          status: 'canceled',
          canceled_at: Timestamp.now(),
          metadata: {
            ...subData.metadata,
            canceled_by: 'subscriptionTestService',
            canceled_at: new Date().toISOString()
          }
        }, { merge: true });
        canceledCount++;
      }
    }

    // Optionally, delete the customer document if no other subscriptions exist
    const allSubs = await getDocs(subscriptionsRef);
    const hasNonTestSubs = allSubs.docs.some(doc => {
      const data = doc.data();
      return !data.metadata?.test && !data.metadata?.created_by;
    });

    if (!hasNonTestSubs && canceledCount > 0) {
      // Option to delete customer document, but let's keep it for now
      // const customerRef = doc(db, 'stripe_customers', userId);
      // await deleteDoc(customerRef);
    }

    console.log(`[Test] Canceled ${canceledCount} test subscription(s) for user ${userId}`);

    return {
      success: true,
      canceledCount
    };
  } catch (error) {
    console.error('[Test] Error canceling subscription:', error);
    throw new Error(`Failed to cancel subscription: ${error.message}`);
  }
}

export default {
  simulateSubscription,
  cancelSimulatedSubscription,
};

